import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, Flame } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

type HomeFeaturedServiceRow = {
  service_id: string;
  service_name: string;
  base_price: number | null;
  company_id: string;
  company_name: string;
  company_slug: string | null;
  city: string | null;
  plan_tier: string | null;
  bookings_count: number;
  next_available_date: string | null; // YYYY-MM-DD
};

const PHOTO_PATHS = [
  "/assets/public-photos/pexels-olly-3772615.jpg",
  "/assets/public-photos/pexels-cottonbro-6466216.jpg",
  "/assets/public-photos/pexels-cottonbro-6466490.jpg",
  "/assets/public-photos/pexels-liliana-drew-9462733.jpg",
  "/assets/public-photos/pexels-cottonbro-6466212.jpg",
  "/assets/public-photos/pexels-cottonbro-6502300.jpg",
  "/assets/public-photos/pexels-matilda-wormwood-4097993.jpg",
  "/assets/public-photos/pexels-tima-miroshnichenko-6196677.jpg",
  "/assets/public-photos/pexels-tima-miroshnichenko-6195278.jpg",
  "/assets/public-photos/pexels-tima-miroshnichenko-6195274.jpg",
  "/assets/public-photos/pexels-tima-miroshnichenko-6197108.jpg",
];

function hash32(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) >>> 0;
}

function ymdToDate(ymd: string): Date {
  // Parse date as local midnight (avoid timezone shifts).
  const [y, m, d] = ymd.split("-").map((n) => Number(n));
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

function availabilityLabel(nextYmd: string | null): string {
  if (!nextYmd) return "Disponibilità: entro 48h";
  const next = ymdToDate(nextYmd);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const diff = Math.round((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "Disponibile oggi";
  if (diff === 1) return "Disponibile domani";
  if (diff === 2) return "Disponibile tra 2 giorni";
  return `Disponibile tra ${diff} giorni`;
}

export function FeaturedServicesCarousel() {
  const navigate = useNavigate();
  const [items, setItems] = useState<HomeFeaturedServiceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        // 1) Try RPC (best: real ranking + availability in one query).
        const { data: rpcData, error: rpcErr } = await (supabase as any).rpc("get_home_featured_services", {
          p_limit: 12,
          p_days: 30,
          p_lookahead_days: 7,
        });

        if (!rpcErr && Array.isArray(rpcData) && rpcData.length > 0) {
          if (active) setItems(rpcData);
          return;
        }

        // 2) Fallback: direct reads (works even if RPC isn't deployed yet).
        const sb: any = supabase;
        const { data: companies, error: cErr } = await sb
          .from("companies")
          .select("id, company_name, city, slug, plan_tier")
          .in("plan_tier", ["PRO", "SCALE", "pro", "scale"])
          .eq("share_enabled", true)
          .eq("is_active", true)
          .limit(30);

        if (cErr || !Array.isArray(companies) || companies.length === 0) {
          if (active) setItems([]);
          return;
        }

        const companyIds = companies.map((c: any) => c.id).filter(Boolean);

        const { data: services, error: sErr } = await sb
          .from("services")
          .select("id, name, base_price, company_id")
          .in("company_id", companyIds)
          .eq("is_active", true)
          .eq("is_public", true)
          .limit(120);

        if (sErr || !Array.isArray(services) || services.length === 0) {
          if (active) setItems([]);
          return;
        }

        // Batch availability (best-effort): earliest unblocked day with capacity > 0.
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        const toYmd = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        const { data: avRows } = await sb
          .from("vendor_availability")
          .select("company_id, date, capacity, blocked")
          .in("company_id", companyIds)
          .gte("date", toYmd(start))
          .lte("date", toYmd(end))
          .eq("blocked", false)
          .gt("capacity", 0)
          .order("date", { ascending: true })
          .limit(500);

        const nextAvailByCompany = new Map<string, string>();
        if (Array.isArray(avRows)) {
          for (const r of avRows) {
            const cid = String((r as any).company_id || "");
            const d = (r as any).date ? String((r as any).date) : null;
            if (!cid || !d) continue;
            if (!nextAvailByCompany.has(cid)) nextAvailByCompany.set(cid, d);
          }
        }

        // Deterministic daily rotation in absence of booking stats.
        const dayKey = toYmd(start);
        const pick = services
          .map((s: any) => {
            const c = companies.find((cc: any) => String(cc.id) === String(s.company_id));
            return {
              service_id: String(s.id),
              service_name: String(s.name || ""),
              base_price: s.base_price != null ? Number(s.base_price) : null,
              company_id: String(s.company_id),
              company_name: String(c?.company_name || ""),
              company_slug: c?.slug ? String(c.slug) : null,
              city: c?.city ? String(c.city) : null,
              plan_tier: c?.plan_tier ? String(c.plan_tier) : null,
              bookings_count: 0,
              next_available_date: nextAvailByCompany.get(String(s.company_id)) || null,
              __rank: hash32(`${dayKey}:${String(s.id)}`),
            } as any;
          })
          .filter((r: any) => r.company_id && r.company_name && r.service_id && r.service_name)
          .sort((a: any, b: any) => a.__rank - b.__rank)
          .slice(0, 12)
          .map(({ __rank, ...rest }: any) => rest);

        if (active) setItems(pick);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const normalized = useMemo(() => {
    return (items || []).filter((r) => r?.service_id && r?.company_id);
  }, [items]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-card">
        Caricamento servizi in evidenza…
      </div>
    );
  }

  if (!normalized.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-card">
        Stiamo preparando i migliori servizi in evidenza. Riprova tra poco.
      </div>
    );
  }

  return (
    <Carousel
      opts={{ align: "start" }}
      className="w-full"
    >
      <CarouselContent>
        {normalized.map((row) => {
          const bg = PHOTO_PATHS[hash32(String(row.service_id)) % PHOTO_PATHS.length];
          const proBadge = (row.plan_tier || "").toString().toUpperCase() === "PRO";
          const hot = Number(row.bookings_count || 0) >= 2;

          return (
            <CarouselItem
              key={`${row.company_id}:${row.service_id}`}
              className="md:basis-1/2 lg:basis-1/3"
            >
              <div className="group relative h-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-card transition-all hover:shadow-medium">
                <button
                  type="button"
                  onClick={() => row.company_slug ? navigate(`/c/${row.company_slug}`) : navigate(`/company/${row.company_id}`)}
                  className="absolute inset-0"
                  aria-label={`Apri ${row.company_name}`}
                />

                <div className="absolute inset-0">
                  <img
                    src={bg}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
                </div>

                <div className="relative flex h-full flex-col p-5">
                  <div className="flex items-center gap-2">
                    {hot ? (
                      <Badge className="bg-white/90 text-gray-900 hover:bg-white/90 gap-1">
                        <Flame className="h-3.5 w-3.5 text-orange-500" />
                        HOT
                      </Badge>
                    ) : (
                      <Badge className="bg-white/90 text-gray-900 hover:bg-white/90">
                        In evidenza
                      </Badge>
                    )}
                    {proBadge ? (
                      <Badge className="bg-primary/90 text-white hover:bg-primary/90">
                        PRO
                      </Badge>
                    ) : (
                      <Badge className="bg-white/90 text-gray-900 hover:bg-white/90">
                        {String(row.plan_tier || "SCALE").toUpperCase()}
                      </Badge>
                    )}
                  </div>

                  <div className="mt-3">
                    <div className="text-lg font-semibold leading-snug text-white line-clamp-2">
                      {row.service_name}
                    </div>
                    <div className="mt-1 text-sm text-white/90">
                      {row.company_name}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-white/90">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{row.city || "Italia"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{availabilityLabel(row.next_available_date)}</span>
                    </div>
                  </div>

                  <div className="mt-auto flex items-end justify-between gap-3 pt-5">
                    <div className="min-w-0">
                      <div className="text-xs text-white/80">A partire da</div>
                      <div className="text-base font-semibold text-white">
                        {row.base_price != null ? formatCurrency(Number(row.base_price)) : "—"}
                      </div>
                      {hot ? (
                        <div className="mt-1 text-xs text-white/80">
                          {row.bookings_count} prenotazioni (ultimi 30 giorni)
                        </div>
                      ) : null}
                    </div>
                    <Button
                      className="relative z-10 bg-primary hover:bg-primary/90"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/booking/${row.company_id}/${row.service_id}`);
                      }}
                    >
                      PRENOTA ORA
                    </Button>
                  </div>
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className="hidden md:flex" />
      <CarouselNext className="hidden md:flex" />
    </Carousel>
  );
}

